import { useEffect, useRef, useState } from 'react'
import { midiToPitch } from './musicTheory'

/**
 * Web MIDI 입력 — 선택된 inputId의 note-on/off를 콜백으로 전달.
 * inputId가 비어 있으면 대기.
 */
export function useMidiInput({
  enabled = true,
  inputId = '',
  keySignature = 'C',
  onNote,
  onNoteOn,
  onNoteOff,
} = {}) {
  const [status, setStatus] = useState('idle') // idle | unsupported | pending | ready | error
  const [deviceName, setDeviceName] = useState(null)
  const onNoteRef = useRef(onNote)
  const onNoteOnRef = useRef(onNoteOn)
  const onNoteOffRef = useRef(onNoteOff)
  const keyRef = useRef(keySignature)

  useEffect(() => { onNoteRef.current = onNote }, [onNote])
  useEffect(() => { onNoteOnRef.current = onNoteOn }, [onNoteOn])
  useEffect(() => { onNoteOffRef.current = onNoteOff }, [onNoteOff])
  useEffect(() => { keyRef.current = keySignature }, [keySignature])

  useEffect(() => {
    if (!enabled) return undefined
    if (typeof navigator === 'undefined' || !navigator.requestMIDIAccess) {
      setStatus('unsupported')
      return undefined
    }

    let cancelled = false
    /** @type {MIDIAccess | null} */
    let access = null
    /** @type {MIDIInput | null} */
    let bound = null

    const handleMessage = (ev) => {
      const data = ev.data
      if (!data || data.length < 2) return
      const statusByte = data[0] & 0xf0
      const note = data[1]
      const velocity = data.length > 2 ? data[2] : 0
      const isNoteOn = statusByte === 0x90 && velocity > 0
      const isNoteOff = statusByte === 0x80 || (statusByte === 0x90 && velocity === 0)
      if (!isNoteOn && !isNoteOff) return
      const pitch = midiToPitch(note, { keySignature: keyRef.current })
      if (!pitch) return
      if (isNoteOn) {
        onNoteOnRef.current?.(pitch, note)
        onNoteRef.current?.(pitch)
      } else {
        onNoteOffRef.current?.(pitch, note)
      }
    }

    const clearBound = () => {
      if (bound) {
        try { bound.onmidimessage = null } catch { /* ignore */ }
        bound = null
      }
    }

    const bindSelected = (midiAccess) => {
      clearBound()
      if (!inputId) {
        setDeviceName(null)
        setStatus(midiAccess.inputs.size ? 'pending' : 'pending')
        return
      }
      const input = midiAccess.inputs.get(inputId)
      if (!input) {
        setDeviceName(null)
        setStatus('pending')
        return
      }
      input.onmidimessage = handleMessage
      bound = input
      setDeviceName(input.name || inputId)
      setStatus('ready')
    }

    setStatus('pending')
    navigator.requestMIDIAccess({ sysex: false })
      .then((midiAccess) => {
        if (cancelled) return
        access = midiAccess
        bindSelected(midiAccess)
        midiAccess.onstatechange = () => {
          if (!cancelled) bindSelected(midiAccess)
        }
      })
      .catch((err) => {
        if (cancelled) return
        console.warn('[midi]', err)
        setStatus('error')
        setDeviceName(null)
      })

    return () => {
      cancelled = true
      clearBound()
      if (access) access.onstatechange = null
    }
  }, [enabled, inputId])

  return { status, deviceName }
}
